import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, Music4, Users, TerminalSquare, ListMusic } from "lucide-react";
import { API } from "../api";
import { Reveal } from "../components/motion";

const inputCls = "w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple";
const labelCls = "text-xs uppercase tracking-[0.15em] text-white/50 block mb-1.5";

export default function Dashboard() {
  const [params] = useSearchParams();
  const [member, setMember] = useState(null);
  const [guilds, setGuilds] = useState([]);
  const [state, setState] = useState("loading"); // loading | connect | unconfigured | ready
  const [discordUrl, setDiscordUrl] = useState(null);
  const [forms, setForms] = useState({});

  useEffect(() => {
    const token = params.get("token");
    if (token) localStorage.setItem("gm_member_token", token);
    const stored = localStorage.getItem("gm_member_token");
    if (!stored) {
      axios.get(`${API}/auth/discord/member/url`)
        .then((r) => { setDiscordUrl(r.data.url); setState("connect"); })
        .catch(() => setState("unconfigured"));
      return;
    }
    axios.get(`${API}/me/guilds`, { headers: { Authorization: `Bearer ${stored}` } })
      .then((r) => {
        setMember(r.data.member);
        setGuilds(r.data.guilds);
        const f = {};
        r.data.guilds.forEach((g) => { f[g.id] = { prefix: g.prefix, dj_role: g.dj_role, music_channel: g.music_channel, default_volume: g.default_volume }; });
        setForms(f);
        setState("ready");
      })
      .catch(() => {
        localStorage.removeItem("gm_member_token");
        axios.get(`${API}/auth/discord/member/url`)
          .then((r) => { setDiscordUrl(r.data.url); setState("connect"); })
          .catch(() => setState("unconfigured"));
      });
  }, [params]);

  const save = async (g) => {
    try {
      await axios.patch(`${API}/me/guilds/${g.id}`, forms[g.id], {
        headers: { Authorization: `Bearer ${localStorage.getItem("gm_member_token")}` },
      });
      toast.success(`${g.name} configuration saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <main className="pt-16 min-h-screen" data-testid="dashboard-page">
      <section className="hero-glow border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.35em] text-volt font-semibold mb-5 flex items-center gap-3">
              <span className="w-8 h-px bg-volt inline-block" /> Server dashboard
            </p>
            <h1 className="font-display text-4xl sm:text-5xl text-white tracking-tight" style={{ fontWeight: 900 }} data-testid="dashboard-headline">
              YOUR SERVERS, <span className="text-outline">YOUR RULES.</span>
            </h1>
            <p className="text-muted-foreground mt-5 max-w-xl text-base md:text-lg">
              Sign in with Discord to configure GrooveMusic per server — prefix, DJ role, music channel and default volume.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        {state === "loading" && <p className="text-white/40 font-mono text-sm">Loading your servers…</p>}

        {state === "unconfigured" && (
          <Reveal>
            <div className="max-w-xl rounded-xl border border-amber-400/25 bg-amber-400/[0.05] p-8 flex gap-4" data-testid="dashboard-unconfigured">
              <AlertCircle className="text-amber-400 shrink-0 mt-1" size={20} />
              <div>
                <h3 className="font-heading font-bold text-white">Discord sign-in isn't connected yet</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  The server dashboard activates the moment Discord OAuth credentials (Client ID + Secret) are added to the site's configuration, with the redirect URI <code className="font-mono text-volt">/api/auth/discord/member/callback</code>. Until then, staff can configure servers from the admin panel.
                </p>
                <Link to="/support" className="inline-block mt-4 text-sm font-semibold text-volt hover:text-white transition-colors duration-150" data-testid="dashboard-contact-link">Contact staff →</Link>
              </div>
            </div>
          </Reveal>
        )}

        {state === "connect" && discordUrl && (
          <Reveal>
            <a href={discordUrl} data-testid="dashboard-discord-connect"
              className="inline-flex items-center gap-2.5 bg-blurple hover:bg-[#4752c4] text-white font-heading font-bold px-8 py-4 rounded-full transition-colors duration-200">
              <Users size={18} /> Sign in with Discord
            </a>
          </Reveal>
        )}

        {state === "ready" && (
          <>
            <p className="text-sm text-white/50 mb-8" data-testid="dashboard-member">
              Signed in as <span className="text-white font-semibold">{member?.name}</span>
            </p>
            {guilds.length === 0 ? (
              <div className="max-w-xl rounded-xl border border-white/10 bg-white/[0.03] p-8" data-testid="dashboard-empty">
                <h3 className="font-heading font-bold text-white">No manageable servers found</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  We couldn't match any servers where you have Manage Server permissions to GrooveMusic's records. Once the live bot syncs your guilds, they'll appear here automatically.
                </p>
              </div>
            ) : (
              <div className="grid lg:grid-cols-2 gap-6" data-testid="dashboard-guilds">
                {guilds.map((g) => (
                  <div key={g.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-7" data-testid={`dashboard-guild-${g.id}`}>
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="font-heading text-lg font-bold text-white">{g.name}</h3>
                      <div className="flex gap-4 text-xs text-white/40 font-mono">
                        <span className="flex items-center gap-1.5"><Users size={12} /> {g.members.toLocaleString()}</span>
                        <span className="flex items-center gap-1.5"><Music4 size={12} /> {g.songs_played.toLocaleString()}</span>
                        <span className="flex items-center gap-1.5"><TerminalSquare size={12} /> {g.commands_used.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={labelCls}>Prefix</label>
                        <input value={forms[g.id]?.prefix || ""} onChange={(e) => setForms({ ...forms, [g.id]: { ...forms[g.id], prefix: e.target.value } })} className={inputCls} data-testid={`guild-prefix-${g.id}`} />
                      </div>
                      <div>
                        <label className={labelCls}>Default volume</label>
                        <input type="number" min="1" max="200" value={forms[g.id]?.default_volume || 80} onChange={(e) => setForms({ ...forms, [g.id]: { ...forms[g.id], default_volume: Number(e.target.value) } })} className={inputCls} data-testid={`guild-volume-${g.id}`} />
                      </div>
                      <div>
                        <label className={labelCls}>DJ role</label>
                        <input value={forms[g.id]?.dj_role || ""} onChange={(e) => setForms({ ...forms, [g.id]: { ...forms[g.id], dj_role: e.target.value } })} placeholder="optional" className={inputCls} data-testid={`guild-dj-${g.id}`} />
                      </div>
                      <div>
                        <label className={labelCls}>Music channel</label>
                        <input value={forms[g.id]?.music_channel || ""} onChange={(e) => setForms({ ...forms, [g.id]: { ...forms[g.id], music_channel: e.target.value } })} placeholder="optional" className={inputCls} data-testid={`guild-channel-${g.id}`} />
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-5">
                      <button onClick={() => save(g)} data-testid={`guild-save-${g.id}`}
                        className="bg-blurple hover:bg-[#4752c4] text-white text-sm font-semibold px-6 py-2.5 rounded-full transition-colors duration-150">
                        Save configuration
                      </button>
                      <span className="inline-flex items-center gap-1.5 text-xs text-white/40"><ListMusic size={13} /> Playlists managed in Discord via !playlist</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
